import { useContext, useEffect } from "react";
import { ContextAuth } from "../../../contexts/ContextAuth";
import {
    savePayment,
    removePayment,
} from "../../../apis/apis.accounting/apisCash";
import { toast } from "react-toastify";

// Bloquear scroll del body cuando el modal está abierto se maneja en el componente

const money = (val) => `S/ ${Number(val || 0).toFixed(2)}`;

const paymentTypeBadge = (type) => {
    if (type === "DY")
        return "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300";
    if (type === "DC")
        return "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300";
    return "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300";
};

const paymentTypeLabel = (type) => {
    if (type === "DC") return "CRÉDITO";
    if (type === "DY") return "YAPE";
    return "EFECTIVO";
};

export function AccountReceiveOperations({
    setCashFlow,
    cashFlow,
    cashFlowSet,
    setCashFlowSet,
    getAllAccountReceiveList,
    getClientsWithDebt,
    isModalOpen,
    setIsModalOpen,
    sellers,
}) {
    const { auth } = useContext(ContextAuth);

    useEffect(() => {
        if (auth && cashFlow.userID === 0) {
            setCashFlow({ ...cashFlow, userID: auth.data.user_id });
        }
    }, [auth, cashFlow, setCashFlow]);

    // Bloquear scroll del body cuando el modal está abierto
    useEffect(() => {
        document.body.style.overflow = isModalOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isModalOpen]);

    const closeModal = () => setIsModalOpen(false);

    function handleChange(e) {
        if (e.target.name === "total") {
            const formattedValue = e.target.value
                .replace(/[^0-9.]/g, "")
                .slice(0, 9);
            setCashFlow({ ...cashFlow, total: formattedValue });
        } else if (e.target.name === "type") {
            const newType = e.target.value;
            const newTotal =
                newType === "DC" && cashFlow.originalPending
                    ? cashFlow.originalPending
                    : cashFlow.total;
            setCashFlow({ ...cashFlow, type: newType, total: newTotal });
        } else {
            setCashFlow({ ...cashFlow, [e.target.name]: e.target.value });
        }
    }

    const handleSave = (e) => {
        e.preventDefault();
        if (Number(cashFlow.operationID) === 0) {
            toast.warning("No hay operación vinculada.");
            return;
        }
        if (Number(cashFlow.total) === 0) {
            toast.warning("No hay monto.");
            return;
        }

        if (cashFlow.originalPending && Number(cashFlow.originalPending) > 0) {
            const totalPending = Number(cashFlow.originalPending);
            const paymentAmount = Number(cashFlow.total);
            if (paymentAmount > totalPending) {
                const overpayment = paymentAmount - totalPending;
                const msg =
                    `⚠️ SOBREPAGO DETECTADO\n\n` +
                    `Monto pendiente: S/ ${totalPending.toFixed(2)}\n` +
                    `Monto a pagar: S/ ${paymentAmount.toFixed(2)}\n` +
                    `Sobrepago: S/ ${overpayment.toFixed(2)}\n\n` +
                    `¿Desea continuar con el sobrepago?`;
                if (!window.confirm(msg)) return;
            }
        }

        savePayment(cashFlow).then(async (res) => {
            setCashFlowSet(res.cashFlowSet);
            getAllAccountReceiveList();
            getClientsWithDebt();
        });
        closeModal();
    };

    const removeAndGetPayments = async (cashFlowID) => {
        try {
            const response = await removePayment({ cashFlowID });
            setCashFlowSet(response);
            const updatedSales = await getAllAccountReceiveList();
            getClientsWithDebt();
            const updatedOperation = updatedSales?.find(
                (op) => Number(op.operationID) === Number(cashFlow.operationID),
            );
            if (updatedOperation) {
                const newPending = Number(
                    updatedOperation.totalInCredit ||
                        updatedOperation.totalPending,
                );
                setCashFlow((prev) => ({
                    ...prev,
                    originalPending: newPending,
                    total:
                        response && response.length === 0
                            ? newPending
                            : prev.total,
                }));
            }
        } catch (error) {
            console.error("Error removing payment:", error);
            toast.error("Error al eliminar el pago");
        }
    };

    const isReviewOnly =
        !cashFlow.originalPending || Number(cashFlow.originalPending) === 0;
    const actualPayments = cashFlowSet?.filter((p) => p.type !== "DC") ?? [];
    const hasExistingPayments = actualPayments.length > 0;
    const isOverpay =
        cashFlow.originalPending &&
        Number(cashFlow.total) > Number(cashFlow.originalPending);

    const inputClass =
        "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
    const labelClass =
        "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";

    if (!isModalOpen) return null;

    return (
        /* Backdrop */
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            aria-modal="true"
            role="dialog"
            aria-labelledby="collectModalLabel"
        >
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={closeModal}
            />

            {/* Modal panel */}
            <div className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 bg-blue-600 dark:bg-blue-700 flex-shrink-0">
                    <h5
                        className="text-base font-semibold text-white flex items-center gap-2"
                        id="collectModalLabel"
                    >
                        <i className="fas fa-credit-card"></i>
                        {isReviewOnly ? "Revisar Pagos" : "Efectuar Pago"}
                    </h5>
                    <button
                        type="button"
                        onClick={closeModal}
                        className="text-white/80 hover:text-white transition-colors p-1 rounded"
                        aria-label="Cerrar"
                    >
                        <i className="fas fa-times text-lg"></i>
                    </button>
                </div>

                {/* Body — scrollable */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 bg-white dark:bg-gray-800">
                    {/* Pending amount info */}
                    {cashFlow.originalPending &&
                        Number(cashFlow.originalPending) > 0 && (
                            <div
                                className={`rounded-xl border px-4 py-3 ${
                                    isOverpay
                                        ? "border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20"
                                        : "border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20"
                                }`}
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">
                                            Monto pendiente original
                                        </p>
                                        <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                                            {money(cashFlow.originalPending)}
                                        </p>
                                    </div>
                                    {isOverpay && (
                                        <div className="sm:ml-auto flex items-center gap-2 rounded-lg bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-600 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                                            <i className="fas fa-exclamation-triangle"></i>
                                            <span>
                                                <strong>Sobrepago:</strong>{" "}
                                                {money(
                                                    Number(cashFlow.total) -
                                                        Number(
                                                            cashFlow.originalPending,
                                                        ),
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                    {/* Review-only notice */}
                    {isReviewOnly && hasExistingPayments && (
                        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
                            <i className="fas fa-check-circle mt-0.5"></i>
                            <div>
                                <p className="font-semibold">
                                    Operación sin saldo pendiente
                                </p>
                                <p className="text-xs mt-0.5 text-emerald-600 dark:text-emerald-400">
                                    Esta operación ya no tiene saldo pendiente.
                                    Puede revisar los pagos realizados.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Payment form */}
                    {!isReviewOnly && (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                                <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                    <i className="fas fa-plus-circle text-blue-500"></i>
                                    Agregar nuevo pago
                                </h6>
                            </div>
                            <div className="px-4 py-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
                                    {/* Tipo */}
                                    <div>
                                        <label
                                            htmlFor="wayPayType"
                                            className={labelClass}
                                        >
                                            <i className="fas fa-credit-card mr-1"></i>
                                            Tipo de pago
                                        </label>
                                        <select
                                            id="wayPayType"
                                            name="type"
                                            value={cashFlow.type}
                                            onChange={handleChange}
                                            className={inputClass}
                                        >
                                            <option value="E">EFECTIVO</option>
                                            <option value="DY">YAPE</option>
                                            {(!cashFlowSet ||
                                                cashFlowSet.length === 0) && (
                                                <option value="DC">
                                                    CRÉDITO
                                                </option>
                                            )}
                                        </select>
                                    </div>

                                    {/* Vendedor/Cobrador */}
                                    <div>
                                        <label
                                            htmlFor="wayPaySeller"
                                            className={labelClass}
                                        >
                                            <i className="fas fa-user-tie mr-1"></i>
                                            Cobrador
                                        </label>
                                        <select
                                            id="wayPaySeller"
                                            name="userID"
                                            value={cashFlow.userID}
                                            onChange={handleChange}
                                            className={inputClass}
                                        >
                                            <option value="0" disabled>
                                                Seleccionar...
                                            </option>
                                            {sellers?.map((s) => (
                                                <option
                                                    key={s.sellerID}
                                                    value={s.sellerID}
                                                >
                                                    {s.sellerName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Descripción */}
                                    <div>
                                        <label
                                            htmlFor="wayPayDescription"
                                            className={labelClass}
                                        >
                                            <i className="fas fa-comment mr-1"></i>
                                            Descripción
                                        </label>
                                        <input
                                            type="text"
                                            id="wayPayDescription"
                                            name="description"
                                            placeholder="Descripción del pago"
                                            value={cashFlow.description}
                                            onChange={handleChange}
                                            onFocus={(e) => e.target.select()}
                                            className={inputClass}
                                        />
                                    </div>

                                    {/* Fecha */}
                                    <div>
                                        <label
                                            htmlFor="transactionDate1"
                                            className={labelClass}
                                        >
                                            <i className="fas fa-calendar mr-1"></i>
                                            Fecha
                                        </label>
                                        <input
                                            type="date"
                                            id="transactionDate1"
                                            name="transactionDate"
                                            value={cashFlow.transactionDate}
                                            onChange={handleChange}
                                            className={inputClass}
                                        />
                                    </div>

                                    {/* Total */}
                                    <div>
                                        <label
                                            htmlFor="wayPayTotal"
                                            className={labelClass}
                                        >
                                            <i className="fas fa-money-bill-wave mr-1"></i>
                                            Total
                                            {isOverpay && (
                                                <span className="ml-1 text-amber-500">
                                                    <i className="fas fa-exclamation-triangle"></i>
                                                </span>
                                            )}
                                        </label>
                                        <input
                                            type="number"
                                            id="wayPayTotal"
                                            name="total"
                                            placeholder="Monto a pagar"
                                            value={cashFlow.total}
                                            onChange={handleChange}
                                            onWheel={(e) =>
                                                e.currentTarget.blur()
                                            }
                                            onFocus={(e) => e.target.select()}
                                            className={`${inputClass} ${isOverpay ? "border-amber-400 focus:ring-amber-400" : ""}`}
                                        />
                                        {isOverpay && (
                                            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                                                <i className="fas fa-info-circle mr-1"></i>
                                                Monto excede el pendiente
                                                original
                                            </p>
                                        )}
                                    </div>

                                    {/* Submit */}
                                    <div>
                                        <button
                                            className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            onClick={handleSave}
                                        >
                                            <i className="fas fa-plus"></i>
                                            Agregar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Payments list */}
                    {hasExistingPayments && (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                                <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                    <i className="fas fa-list text-blue-500"></i>
                                    Pagos realizados ({actualPayments.length})
                                </h6>
                            </div>

                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto bg-white dark:bg-gray-900">
                                <table className="min-w-full text-sm border-separate border-spacing-0">
                                    <thead>
                                        <tr>
                                            {[
                                                "#",
                                                "Descripción",
                                                "Fecha",
                                                "Tipo",
                                                "Cobrador",
                                                "Total",
                                                "Acción",
                                            ].map((h) => (
                                                <th
                                                    key={h}
                                                    className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-center"
                                                >
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {actualPayments.map((item, c) => (
                                            <tr
                                                key={c}
                                                className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                            >
                                                <td className="px-3 py-2 text-xs text-center font-semibold text-blue-600 dark:text-blue-400 border-b border-gray-100 dark:border-gray-700">
                                                    {item.id}
                                                </td>
                                                <td
                                                    className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700 max-w-[200px] truncate"
                                                    title={item.description}
                                                >
                                                    {item.description}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                                    {item.transactionDate}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center border-b border-gray-100 dark:border-gray-700">
                                                    <span
                                                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${paymentTypeBadge(item.type)}`}
                                                    >
                                                        {paymentTypeLabel(
                                                            item.type,
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700">
                                                    {item.user}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-right font-semibold text-emerald-700 dark:text-emerald-400 border-b border-gray-100 dark:border-gray-700">
                                                    {money(item.total)}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center border-b border-gray-100 dark:border-gray-700">
                                                    <button
                                                        type="button"
                                                        title="Eliminar pago"
                                                        onClick={() => {
                                                            const msg =
                                                                actualPayments.length ===
                                                                1
                                                                    ? "⚠️ ATENCIÓN: Está a punto de eliminar el ÚNICO pago. La venta quedará pendiente. ¿Continuar?"
                                                                    : "¿Está seguro de eliminar este pago?";
                                                            if (
                                                                window.confirm(
                                                                    msg,
                                                                )
                                                            )
                                                                removeAndGetPayments(
                                                                    item.id,
                                                                );
                                                        }}
                                                        className="inline-flex items-center gap-1 rounded-md border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs px-2 py-1 transition-colors"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-900">
                                {actualPayments.map((item, c) => (
                                    <div key={c} className="p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <p className="text-xs font-bold text-blue-600 dark:text-blue-400">
                                                    #{item.id}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                    {item.description}
                                                </p>
                                                {item.user && (
                                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                                        <i className="fas fa-user-tie mr-1"></i>
                                                        {item.user}
                                                    </p>
                                                )}
                                            </div>
                                            <span
                                                className={`text-xs font-medium px-2 py-0.5 rounded-full ${paymentTypeBadge(item.type)}`}
                                            >
                                                {paymentTypeLabel(item.type)}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                <i className="fas fa-calendar mr-1"></i>
                                                {item.transactionDate}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                                    {money(item.total)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const msg =
                                                            actualPayments.length ===
                                                            1
                                                                ? "⚠️ ATENCIÓN: Está a punto de eliminar el ÚNICO pago. La venta quedará pendiente. ¿Continuar?"
                                                                : "¿Está seguro de eliminar este pago?";
                                                        if (window.confirm(msg))
                                                            removeAndGetPayments(
                                                                item.id,
                                                            );
                                                    }}
                                                    className="inline-flex items-center gap-1 rounded-md border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs px-2 py-1 transition-colors"
                                                >
                                                    <i className="fas fa-trash mr-1"></i>
                                                    Eliminar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Empty state */}
                    {!hasExistingPayments && (
                        <div className="py-10 text-center">
                            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-700 mb-3">
                                <i className="fas fa-receipt text-gray-400 dark:text-gray-500 text-xl"></i>
                            </div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                No hay pagos registrados
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                {isReviewOnly
                                    ? "Esta operación no tiene pagos registrados."
                                    : "Agregue un pago usando el formulario de arriba."}
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium px-4 py-2 transition-colors"
                    >
                        <i className="fas fa-times"></i>
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
}
